import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, useSearchParams } from 'react-router-dom';
import { i18n } from '@international/mastodon-i18n';
import styled from 'styled-components';

import Loading from 'components/Loading';
import MainContainer from 'components/MainContainer';
import {
  exchangeWorkOSOAuthCode,
  isWorkosDobPending,
  workosReset,
  workosVerifyFailure,
} from 'actions/workosLogin';
import { trackAnalyticsEvent } from 'actions/analytics';
import { ANALYTICS_EVENTS, ANALYTICS_PROPERTIES } from 'lib/analytics';
import useAuthGateState from 'hooks/useAuthGateState';
import { safeReturnPath } from 'utils/returnPath';
import { isSessionStorageSupported } from 'lib/helpers';
import {
  AUTH_METHODS,
  PATHS,
  SESSION_STORAGE_OAUTH_PREV_LOCATION_KEY,
} from '../../constants';

interface RedirectingProps {
  state?: {
    fromOAuthCallback: boolean;
    prevLocation?: string;
  };
  to: string;
}

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export function Redirecting({ state, to }: RedirectingProps) {
  return (
    <MainContainer data-testid='workos-login-callback'>
      <div
        aria-live='polite'
        data-testid='workos-login-redirect-status'
        role='status'
      >
        <VisuallyHidden>
          {i18n.getStr('components.WorkOSLogin.redirecting')}
        </VisuallyHidden>
        <Loading role='presentation' />
      </div>
      <Navigate replace state={state} to={to} />
    </MainContainer>
  );
}

const WorkOSLoginCallback: React.FC = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const oauthError = searchParams.get('error');
  const {
    candidateLogin: { loggedIn, workosError, workosStep },
  } = useAuthGateState();

  const prevLocation =
    safeReturnPath(
      isSessionStorageSupported()
        ? window.sessionStorage.getItem(SESSION_STORAGE_OAUTH_PREV_LOCATION_KEY)
        : null,
    ) ?? undefined;

  useEffect(() => {
    if (oauthError) {
      // Reset first so a stale DOB step/pending flag from a prior exchange
      // can't keep the DOB form active; then record the denial as the error.
      dispatch(workosReset());
      dispatch(workosVerifyFailure('oauthError'));
      dispatch(
        trackAnalyticsEvent(
          ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_GOOGLE_OAUTH_FAILED,
          {
            [ANALYTICS_PROPERTIES.AUTH_METHOD]: AUTH_METHODS.WORKOS_GOOGLE,
            errorType: 'google_denied',
            errorDetail: oauthError,
          },
        ),
      );
    } else if (code && !isWorkosDobPending()) {
      dispatch(exchangeWorkOSOAuthCode(code));
    }
  }, [code, oauthError, dispatch]);

  useEffect(() => {
    if (loggedIn || workosError) {
      window.sessionStorage.removeItem(SESSION_STORAGE_OAUTH_PREV_LOCATION_KEY);
    }
  }, [loggedIn, workosError]);

  if (loggedIn) {
    return <Redirecting to={prevLocation || PATHS.REPORTS_OVERVIEW} />;
  }

  // Error, DOB challenge, and missing-code paths all return to the login page
  // with the same callback state so WorkOSLogin can restore or show the error.
  if (
    oauthError ||
    workosError ||
    isWorkosDobPending() ||
    workosStep === 'dob' ||
    !code
  ) {
    return (
      <Redirecting
        state={{ fromOAuthCallback: true, prevLocation }}
        to={PATHS.WORKOS_LOGIN}
      />
    );
  }

  return (
    <MainContainer data-testid='workos-login-callback'>
      <Loading />
    </MainContainer>
  );
};

export default WorkOSLoginCallback;
